import { useState, useEffect, useRef, useCallback } from 'react'
import { Modal } from '@/shared/components/Modal'
import { ColorPicker } from './ColorPicker'
import { useRegistryStore } from '../stores/registryStore'
import { api, getErrorMessage } from '@/shared/api/generated'
import type { Tag, Value, RecordRow } from '@/shared/api/generated'
import { toast } from '@/shared/stores/toastStore'

interface RecordEditorProps {
  record: RecordRow | null  // null = create mode
  groupId: number
  onClose: () => void
  onSaved: () => void
}

interface TagInputState {
  selectedIds: number[]  // value IDs
  freeText: string       // for editable tags: uncommitted text
}

export function RecordEditor({ record, groupId, onClose, onSaved }: RecordEditorProps) {
  const { config, tags, values, loadAllValues } = useRegistryStore()
  const [tagInputs, setTagInputs] = useState<Record<number, TagInputState>>({})
  const [color, setColor] = useState(record?.color || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [loadingValues, setLoadingValues] = useState(false)
  const commentRef = useRef<HTMLDivElement>(null)

  const entityName = config?.entity_name || 'Record'

  // Load all values on mount, then initialize tag inputs
  useEffect(() => {
    let cancelled = false
    setLoadingValues(true)
    loadAllValues(groupId).then(async () => {
      if (cancelled) return
      if (record) {
        // Edit mode: fetch value_ids from GetRecord
        try {
          const full = await api.getRecord(record.id)
          if (!cancelled) initFromValueIds(full.value_ids || [])
        } catch (err) {
          toast.error(getErrorMessage(err))
          if (!cancelled) initFromValueIds([])
        }
      } else {
        initFromValueIds([])
      }
      if (!cancelled) setLoadingValues(false)
    })
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function initFromValueIds(valueIds: number[]) {
    const inputs: Record<number, TagInputState> = {}
    for (const tag of tags) {
      inputs[tag.id] = { selectedIds: [], freeText: '' }
    }
    const storeValues = useRegistryStore.getState().values
    for (const vid of valueIds) {
      for (const tag of tags) {
        if ((storeValues[tag.id] || []).some(v => v.id === vid)) {
          const entry = inputs[tag.id]
          if (entry) entry.selectedIds.push(vid)
          break
        }
      }
    }
    setTagInputs(inputs)
  }

  // Set initial comment content after mount
  useEffect(() => {
    if (commentRef.current && record?.comment) {
      commentRef.current.innerHTML = record.comment
    }
  }, [loadingValues]) // eslint-disable-line react-hooks/exhaustive-deps

  // Paste as plain text to avoid injecting arbitrary HTML
  const handleCommentPaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
  }, [])

  function setTagInput(tagId: number, update: Partial<TagInputState>) {
    setTagInputs(prev => {
      const cur = prev[tagId] || { selectedIds: [], freeText: '' }
      return { ...prev, [tagId]: { selectedIds: cur.selectedIds, freeText: cur.freeText, ...update } }
    })
  }

  function addValueToTag(tagId: number, valueId: number, multiple: boolean) {
    setTagInputs(prev => {
      const cur = prev[tagId] || { selectedIds: [], freeText: '' }
      if (cur.selectedIds.includes(valueId)) return prev
      return {
        ...prev,
        [tagId]: {
          ...cur,
          selectedIds: multiple ? [...cur.selectedIds, valueId] : [valueId],
          freeText: '',
        },
      }
    })
  }

  function removeValueFromTag(tagId: number, valueId: number) {
    setTagInputs(prev => {
      const cur = prev[tagId] || { selectedIds: [], freeText: '' }
      return {
        ...prev,
        [tagId]: { ...cur, selectedIds: cur.selectedIds.filter(id => id !== valueId) },
      }
    })
  }

  // Validate required tags
  function validate(): string | null {
    for (const tag of tags) {
      if (tag.required) {
        const input = tagInputs[tag.id]
        if (!input || (input.selectedIds.length === 0 && !input.freeText.trim())) {
          return `${tag.name} is required`
        }
      }
    }
    return null
  }

  async function handleSave() {
    const validationError = validate()
    if (validationError) { setError(validationError); return }

    setSaving(true)
    setError('')

    try {
      // For editable tags with freeText, create the value first
      const allValueIds: number[] = []
      for (const tag of tags) {
        const input = tagInputs[tag.id] || { selectedIds: [], freeText: '' }
        allValueIds.push(...input.selectedIds)

        if (tag.editable && input.freeText.trim()) {
          const created = await api.createValue(tag.id, {
            value: input.freeText.trim(),
            persistent: false,
            group_id: groupId,
          })
          allValueIds.push(created.id)
        }
      }

      const req = {
        group_id: groupId,
        comment: commentRef.current?.innerHTML || '',
        color,
        value_ids: allValueIds,
      }

      if (record) {
        await api.updateRecord(record.id, req)
      } else {
        await api.createRecord(req)
      }

      onSaved()
      onClose()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const title = record
    ? `Edit ${entityName} ${record.path?.replace(/\/$/, '') || record.id}`
    : `New ${entityName}`

  return (
    <Modal title={title} onClose={onClose} wide>
      <div className="record-editor" data-testid="record-editor">
        {loadingValues ? (
          <div className="record-editor-loading">Loading…</div>
        ) : (
          <>
            <div className="record-editor-tags">
              {tags.map(tag => (
                <TagField
                  key={tag.id}
                  tag={tag}
                  input={tagInputs[tag.id] || { selectedIds: [], freeText: '' }}
                  allValues={values[tag.id] || []}
                  onAddValue={(vid) => addValueToTag(tag.id, vid, tag.multiple)}
                  onRemoveValue={(vid) => removeValueFromTag(tag.id, vid)}
                  onFreeTextChange={(text) => setTagInput(tag.id, { freeText: text })}
                  groupId={groupId}
                />
              ))}
            </div>

            <div className="record-editor-section">
              <label>Color</label>
              <ColorPicker colors={config?.colors || []} value={color} onChange={setColor} />
            </div>

            <div className="record-editor-section">
              <label>Comment</label>
              <div
                ref={commentRef}
                className="record-editor-comment"
                contentEditable
                onPaste={handleCommentPaste}
              />
            </div>

            {error && <div className="record-editor-error">{error}</div>}

            <div className="record-editor-actions">
              <button onClick={onClose} disabled={saving}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="editor-save">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

// --- TagField: per-tag input with autocomplete ---

function TagField({ tag, input, allValues, onAddValue, onRemoveValue, onFreeTextChange, groupId }: {
  tag: Tag
  input: TagInputState
  allValues: Value[]
  onAddValue: (valueId: number) => void
  onRemoveValue: (valueId: number) => void
  onFreeTextChange: (text: string) => void
  groupId: number
}) {
  const [query, setQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [localValues, setLocalValues] = useState<Value[]>([])
  const inputRef = useRef<HTMLInputElement>(null)

  const combined = [...allValues, ...localValues.filter(lv => !allValues.some(av => av.id === lv.id))]
  const selectedValues = combined.filter(v => input.selectedIds.includes(v.id))
  const available = combined.filter(v =>
    !input.selectedIds.includes(v.id) &&
    (!query || v.value.toLowerCase().includes(query.toLowerCase()))
  )

  function handleSelect(valueId: number) {
    onAddValue(valueId)
    setQuery('')
    setShowDropdown(false)
    inputRef.current?.focus()
  }

  function handleInputChange(text: string) {
    setQuery(text)
    setShowDropdown(true)
    if (tag.editable) onFreeTextChange(text)
  }

  async function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Tab' && available.length > 0) {
      e.preventDefault()
      const first = available[0]
      if (first) handleSelect(first.id)
    } else if (e.key === 'Enter' && query.trim()) {
      e.preventDefault()
      // Exact match takes priority
      const exact = available.find(v => v.value.toLowerCase() === query.trim().toLowerCase())
      if (exact) {
        handleSelect(exact.id)
      } else if (tag.editable) {
        // Editable tag: create new value
        try {
          const created = await api.createValue(tag.id, { value: query.trim(), persistent: false, group_id: groupId })
          setLocalValues(prev => [...prev, created])
          onAddValue(created.id)
          setQuery('')
          onFreeTextChange('')
        } catch (err) { toast.error(getErrorMessage(err)) }
      } else if (available.length > 0) {
        // Non-editable: select first partial match
        const first = available[0]
        if (first) handleSelect(first.id)
      }
    }
  }

  function handleBlur() {
    setTimeout(() => {
      // Auto-select exact match on blur
      if (query) {
        const exact = available.find(v => v.value.toLowerCase() === query.toLowerCase())
        if (exact) {
          handleSelect(exact.id)
          return
        }
      }
      setShowDropdown(false)
    }, 150)
  }

  return (
    <div className="record-editor-field">
      <label>
        {tag.name}
        {tag.required && <span className="required-mark">*</span>}
      </label>
      <div className="tag-field-body">
        {tag.multiple && selectedValues.length > 0 && (
          <div className="tag-field-chips">
            {selectedValues.map(v => (
              <span key={v.id} className="tag-chip">
                {v.value}
                <button type="button" onClick={() => onRemoveValue(v.id)}>✕</button>
              </span>
            ))}
          </div>
        )}
        <div className="filter-autocomplete">
            <input
              ref={inputRef}
              type="text"
              value={!tag.multiple && input.selectedIds.length > 0
                ? selectedValues[0]?.value ?? ''
                : query}
              placeholder={tag.editable ? `Type or select ${tag.name}` : `Select ${tag.name}`}
              onChange={e => {
                if (!tag.multiple && input.selectedIds.length > 0 && input.selectedIds[0] !== undefined) {
                  onRemoveValue(input.selectedIds[0])
                }
                handleInputChange(e.target.value)
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
            />
            {showDropdown && available.length > 0 && (
              <ul className="autocomplete-list">
                {available.slice(0, 15).map(v => (
                  <li key={v.id} onMouseDown={() => handleSelect(v.id)}>{v.value}</li>
                ))}
              </ul>
            )}
          </div>
        {!tag.multiple && input.selectedIds.length > 0 && (
          <button type="button" className="tag-clear" onClick={() => {
            if (input.selectedIds[0] !== undefined) onRemoveValue(input.selectedIds[0])
            setQuery('')
          }}>✕</button>
        )}
      </div>
    </div>
  )
}
